import * as React from 'react';
import { Dialog as DialogPrimitive } from '@base-ui-components/react/dialog';
import { XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT } from '@shared/i18n';

import cardStyles from './dialog-card.module.css';

/**
 * Base UI's Dialog maps onto Radix's almost one-to-one, with two renames:
 * `Overlay` -> `Backdrop`, `Content` -> `Popup`. The bigger difference is
 * invisible at the call sites below but matters for the CSS: Radix expressed
 * open/closed as `data-state="open"|"closed"` (one attribute, two values);
 * Base UI uses separate boolean-presence attributes -- `data-open`,
 * `data-closed`, `data-starting-style`, `data-ending-style` -- so every
 * `data-[state=open]:`/`data-[state=closed]:` Tailwind variant below became
 * `data-[open]:`/`data-[closed]:` (attribute *presence*, not a value match).
 */

/**
 * `duration-200` matches the popup's, and `fill-mode-forwards` holds the end
 * of the exit animation. Base UI unmounts both parts together, once every
 * exit animation has finished; the backdrop used to run tw-animate-css's
 * default 150ms against the popup's 200ms, so it finished first and snapped
 * back to full opacity until the popup was done -- a dark flash at the end of
 * every close.
 */
const BACKDROP_CLASS_NAME =
	'data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[closed]:fill-mode-forwards data-[open]:fade-in-0 fixed inset-0 z-50 bg-black/50 duration-200';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

const DialogTrigger = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>
>(({ ...props }, ref) => {
	return (
		<DialogPrimitive.Trigger
			ref={ref}
			data-slot="dialog-trigger"
			{...props}
		/>
	);
});
DialogTrigger.displayName = 'DialogTrigger';

function DialogPortal({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
	return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

const DialogClose = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Close>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ ...props }, ref) => {
	return (
		<DialogPrimitive.Close ref={ref} data-slot="dialog-close" {...props} />
	);
});
DialogClose.displayName = 'DialogClose';

const DialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Backdrop>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Backdrop>
>(({ className, ...props }, ref) => {
	return (
		<DialogPrimitive.Backdrop
			ref={ref}
			data-slot="dialog-overlay"
			className={cn(
				BACKDROP_CLASS_NAME,
				className,
			)}
			{...props}
		/>
	);
});
DialogOverlay.displayName = 'DialogOverlay';

const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Popup>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Popup> & {
		showCloseButton?: boolean;
		backdropClassName?: string;
	}
>(({ className, children, showCloseButton = true, backdropClassName, ...props }, ref) => {
	const t = useT();
	return (
		<DialogPrimitive.Portal data-slot="dialog-portal">
			<DialogPrimitive.Backdrop
				data-slot="dialog-overlay"
				className={cn(
					BACKDROP_CLASS_NAME,
					backdropClassName,
				)}
			/>
			<DialogPrimitive.Popup
				ref={ref}
				data-slot="dialog-content"
				className={cn(
					'bg-background data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0 data-[closed]:zoom-out-95 data-[closed]:fill-mode-forwards data-[open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg',
					className,
				)}
				{...props}
			>
				{children}
				{showCloseButton && (
					<DialogPrimitive.Close
						data-slot="dialog-close"
						className="ring-offset-background focus-visible:ring-ring data-[open]:bg-accent data-[open]:text-muted-foreground absolute top-4 end-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
					>
						<XIcon />
						<span className="sr-only">{t('common.close')}</span>
					</DialogPrimitive.Close>
				)}
			</DialogPrimitive.Popup>
		</DialogPrimitive.Portal>
	);
});
DialogContent.displayName = 'DialogContent';

const DialogHeader = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			data-slot="dialog-header"
			className={cn(
				'flex flex-col gap-2 text-center sm:text-start',
				className,
			)}
			{...props}
		/>
	);
});
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			data-slot="dialog-footer"
			className={cn(
				'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
				className,
			)}
			{...props}
		/>
	);
});
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
	return (
		<DialogPrimitive.Title
			ref={ref}
			data-slot="dialog-title"
			className={cn('text-lg leading-none font-semibold', className)}
			{...props}
		/>
	);
});
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
	return (
		<DialogPrimitive.Description
			ref={ref}
			data-slot="dialog-description"
			className={cn('text-muted-foreground text-sm', className)}
			{...props}
		/>
	);
});
DialogDescription.displayName = 'DialogDescription';

/**
 * The card look the old hand-rolled `Modal` had (fixed-size, padded, block
 * layout). Pass it to `DialogContent` alongside a caller's own class, with
 * `dialogCardBackdropClassName` as its `backdropClassName`.
 */
export const dialogCardClassName = cardStyles['card'];
/**
 * `Modal` was a native `<dialog>` in the browser's top layer, above every
 * z-index -- including the reader menu (100) and the topbar (999). These
 * dialogs open from those menus, so they're raised to match, like
 * `drawer.tsx` does. The shared `z-50` default is left alone for the other
 * dialogs, which may rely on sitting under the topbar.
 */
export const dialogCardBackdropClassName = cardStyles['backdrop'];

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
};
